import { Button } from '@immo/ui-kit';

type Option<T extends string | number> = {
  readonly value: T;
  readonly label: string;
};

type Props<T extends string | number> = {
  readonly options: ReadonlyArray<Option<T>>;
  readonly value: T;
  readonly onChange: (value: T) => void;
  readonly ariaLabel: string;
};

/**
 * A `role="group"` of `Button`s rather than a `NativeSelect` — comparing the
 * options is the point, so both stay visible (IMM-910.8 Decisions). Built
 * from `@immo/ui-kit`'s `Button` to satisfy `check:raw-button` (BL-105).
 * Re-clicking the active segment is a no-op (BL-101) — `Neu holen` is the
 * affordance for re-issuing the same choice.
 *
 * Below `sm` the group spans the full width and the segments share it evenly:
 * at 390px an `inline-flex` group of three sits in a row that has nothing else
 * in it anyway, and equal thirds are the larger touch target.
 */
export function Segments<T extends string | number>({ options, value, onChange, ariaLabel }: Props<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="flex w-full gap-0.5 rounded-md bg-neutral-100 p-0.5 sm:inline-flex sm:w-auto"
    >
      {options.map((option) => {
        const active = option.value === value;

        return (
          <Button
            key={option.value}
            type="button"
            size="sm"
            variant={active ? 'secondary' : 'ghost'}
            className="flex-1 sm:flex-none"
            aria-pressed={active}
            onClick={() => {
              if (active) return;

              onChange(option.value);
            }}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}
